import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { TrophyIcon, MedalIcon, CrownIcon, FireIcon } from "@phosphor-icons/react";

const SCOPES = [
  { key: "month", label: "This month" },
  { key: "all_time", label: "All-time" },
];

export default function Leaderboard() {
  const [scope, setScope] = useState("month");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get(`/leaderboard/recruiters?scope=${scope}&limit=50`)
      .then((r) => {
        if (alive) setItems(r.data?.items || []);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [scope]);

  return (
    <div className="min-h-screen bg-white" data-testid="leaderboard-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-10 sm:py-14">
        <div className="flex items-center gap-3 text-amber-600 mb-2">
          <TrophyIcon size={26} weight="fill" />
          <p className="overline text-zinc-500">Recruit leaderboard</p>
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-zinc-950">
          Hall of fame.
        </h1>
        <p className="mt-3 text-zinc-600 max-w-xl leading-relaxed">
          Bidorah grows because of the people who bring real ones in. These are
          the recruiters making it happen. Top 3 each month split a $500 cash
          bonus on top of their accumulated credit.
        </p>

        {/* Scope toggle */}
        <div className="mt-8 inline-flex rounded-full border border-zinc-200 p-1 bg-white" data-testid="leaderboard-scope-toggle">
          {SCOPES.map((s) => (
            <button
              key={s.key}
              onClick={() => setScope(s.key)}
              className={`px-4 py-2 text-sm font-semibold rounded-full transition-colors ${
                scope === s.key
                  ? "bg-zinc-950 text-white"
                  : "text-zinc-700 hover:text-zinc-950"
              }`}
              data-testid={`scope-btn-${s.key}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Top 3 podium */}
        {!loading && items.length > 0 && <Podium items={items.slice(0, 3)} />}

        {/* Full list */}
        <div className="mt-8 border border-zinc-200 rounded-2xl overflow-hidden bg-white">
          {loading ? (
            <div className="p-10 text-center text-zinc-500">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-zinc-500" data-testid="leaderboard-empty">
              No recruiters {scope === "month" ? "this month" : "yet"}. Be the first.{" "}
              <Link to="/refer" className="underline font-semibold text-[#1D78AB]">
                Get your invite link →
              </Link>
            </div>
          ) : (
            items.map((it) => <Row key={it.user_id} item={it} />)
          )}
        </div>

        <div className="mt-8 flex justify-center">
          <Link to="/refer">
            <Button className="bg-[#1D78AB] hover:bg-[#165d85] text-white rounded-full h-12 px-6 gap-2">
              <FireIcon size={16} weight="fill" /> Get on this list
            </Button>
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Podium({ items }) {
  // Top 3 visualized as a podium row (2nd, 1st, 3rd L→R for visual hierarchy).
  const order = items.length === 3 ? [items[1], items[0], items[2]] : items;
  return (
    <div className="mt-10 grid grid-cols-3 gap-3 sm:gap-5 items-end" data-testid="leaderboard-podium">
      {order.map((it, i) => {
        // Determine the actual rank from the position in the visual order.
        const isFirst = it === items[0];
        const isSecond = it === items[1];
        const heightCls = isFirst ? "h-44 sm:h-56" : isSecond ? "h-36 sm:h-48" : "h-32 sm:h-40";
        const bg = isFirst
          ? "bg-gradient-to-b from-amber-300 to-amber-500"
          : isSecond
          ? "bg-gradient-to-b from-zinc-300 to-zinc-400"
          : "bg-gradient-to-b from-amber-700 to-amber-800";
        return (
          <div key={it.user_id} className="flex flex-col items-center" data-testid={`podium-${it.rank}`}>
            <div className={`relative h-14 w-14 sm:h-20 sm:w-20 rounded-full ring-4 ${
              isFirst ? "ring-amber-300" : isSecond ? "ring-zinc-300" : "ring-amber-700"
            } overflow-hidden bg-zinc-100`}>
              {it.picture ? (
                <img src={it.picture} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="h-full w-full grid place-items-center text-zinc-400 font-mono">@</div>
              )}
              {isFirst && (
                <CrownIcon
                  size={28}
                  weight="fill"
                  className="absolute -top-3 left-1/2 -translate-x-1/2 text-amber-400 drop-shadow-md"
                />
              )}
            </div>
            <div className="font-display font-bold text-sm sm:text-base text-zinc-950 mt-2 truncate max-w-[120px]">
              @{it.username}
            </div>
            <div className="text-xs text-zinc-500">{it.points} pts</div>
            <div className={`${heightCls} ${bg} w-full rounded-t-xl mt-3 grid place-items-center text-white font-display font-black text-3xl sm:text-5xl shadow-lg`}>
              #{it.rank}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Row({ item }) {
  return (
    <Link
      to={`/seller/${item.user_id}`}
      className="flex items-center gap-3 sm:gap-4 px-4 py-3 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 transition-colors"
      data-testid={`leaderboard-row-${item.rank}`}
    >
      <div className="w-9 sm:w-10 text-center">
        {item.rank <= 3 ? (
          <MedalIcon
            size={22}
            weight="fill"
            className={
              item.rank === 1
                ? "text-amber-500 mx-auto"
                : item.rank === 2
                ? "text-zinc-400 mx-auto"
                : "text-amber-700 mx-auto"
            }
          />
        ) : (
          <span className="font-mono text-zinc-400 font-semibold">{item.rank}</span>
        )}
      </div>
      <div className="h-10 w-10 rounded-full bg-zinc-100 overflow-hidden flex-shrink-0">
        {item.picture ? (
          <img src={item.picture} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full grid place-items-center text-zinc-400 font-mono">@</div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-zinc-950 text-sm truncate">@{item.username}</div>
        <div className="text-[11px] text-zinc-500">
          {item.referrals} invite{item.referrals === 1 ? "" : "s"} ·{" "}
          {item.verified_count} verified
          {item.is_founder && (
            <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded font-semibold">
              Founder
            </span>
          )}
        </div>
      </div>
      <div className="font-mono font-black text-lg text-zinc-950 tabular-nums">
        {item.points}
      </div>
    </Link>
  );
}
